import React, { useEffect, useState } from 'react';
import Drawer from '@material-ui/core/Drawer';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';


const CATEGORY_CONFIG = {
  table: { label: 'Table', color: '#2563EB', bgColor: '#EFF6FF', icon: 'table_chart' },
  view: { label: 'View', color: '#10B981', bgColor: '#ECFDF5', icon: 'visibility' },
  procedure: { label: 'Stored Proc', color: '#8B5CF6', bgColor: '#F5F3FF', icon: 'settings' },
  function: { label: 'Function', color: '#F59E0B', bgColor: '#FEF3C7', icon: 'functions' }
};

const getCategoryInfo = (type) => {
  const typeLower = (type || '').toLowerCase();
  for (const [key, config] of Object.entries(CATEGORY_CONFIG)) {
    if (typeLower.includes(key)) {
      return config;
    }
  }
  return { label: type || 'Unknown', color: '#6b7280', bgColor: '#f3f4f6', icon: 'help' };
};

// Format a MySQL DATETIME into a short "DD Mon YYYY, HH:MM" label.
const formatHistoryDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// Render a diff value: dash for null, JSON for objects/arrays, otherwise as-is.
const formatDiffValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
};

// Show the field-level diff (what actually changed); fall back to the
// human description when no field-level diff is available.
const summarizeChange = (entry) => {
  if (Array.isArray(entry.changedFields) && entry.changedFields.length > 0) {
    return entry.changedFields
      .map((f) => `${f.field}: ${formatDiffValue(f.from)} → ${formatDiffValue(f.to)}`)
      .join('; ');
  }
  return entry.changeDescription || '-';
};

const ObjectDetailSheet = ({
  open, object, details, isLoading, onClose, onNavigate, objects, objectRelated, isLoadingRelated, onLoadRelated,
  objectHistory, isLoadingHistory, onLoadHistory,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasLoadedRelated, setHasLoadedRelated] = useState(false);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const [expandedTriggerStatements, setExpandedTriggerStatements] = useState({});
  
  // Filter predicate for dynamically-rendered column keys:
  // excludes businessContext, ID fields, and boolean flag keys handled separately (PII, AI Eligible)
  const isAutoRenderedKey = (key) =>
    key !== 'businessContext' &&
    !key.toLowerCase().includes('id') &&
    key !== 'isPii' &&
    key !== 'is_pii' &&
    key !== 'isAiEligible' &&
    key !== 'is_ai_eligible';

  if (!object) return null;

  const categoryInfo = getCategoryInfo(object.type || object.objectType);
  const objectDetails = details || object;
  const objectId = objectDetails.objectId || objectDetails.id || object.objectId || object.id;

  useEffect(() => {
    setHasLoadedRelated(false);
    setHasLoadedHistory(false);
    setExpandedTriggerStatements({});
  }, [objectId]);

  useEffect(() => {
    if (isExpanded && onLoadRelated && !hasLoadedRelated && objectId) {
      onLoadRelated(objectId);
      setHasLoadedRelated(true);
    }
  }, [isExpanded, onLoadRelated, hasLoadedRelated, objectId]);

  useEffect(() => {
    if (isExpanded && onLoadHistory && !hasLoadedHistory && objectId) {
      onLoadHistory(objectId);
      setHasLoadedHistory(true);
    }
  }, [isExpanded, onLoadHistory, hasLoadedHistory, objectId]);
  
  const toggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
  };

  // Find current object index
  const currentIndex = objects ? objects.findIndex(obj => 
    (obj.objectId || obj.id) === (object.objectId || object.id)
  ) : -1;
  
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (objects?.length - 1);

  const toggleTriggerStatement = (triggerKey) => {
    setExpandedTriggerStatements((prev) => ({
      ...prev,
      [triggerKey]: !prev[triggerKey],
    }));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="object-detail-sheet"
      PaperProps={{
        style: { width: isExpanded ? '100%' : '40%', minWidth: '400px' }
      }}
    >
      <div className="detail-sheet-container" data-testid="dd-detail-sheet">
        {/* Header */}
        <div className="detail-sheet-header">
          <div className="detail-sheet-header-content">
            <div className="detail-sheet-title">
              <div className="title-with-type">
                <h2>{objectDetails.displayName || objectDetails.name}</h2>
                <Chip
                  label={categoryInfo.label}
                  size="small"
                  style={{
                    backgroundColor: categoryInfo.bgColor,
                    color: categoryInfo.color,
                    fontWeight: 500,
                    fontSize: '11px',
                    height: '22px'
                  }}
                />
              </div>
              <a href="#" onClick={(e) => { e.preventDefault(); toggleExpanded(); }} className="expand-link">
                {isExpanded ? 'Show less details' : 'Show more details'}
              </a>
            </div>
            <div className="detail-sheet-actions">
              <IconButton 
                onClick={() => onNavigate && onNavigate('prev')} 
                size="small"
                disabled={!hasPrevious}
                title="Previous object"
              >
                <i className="material-icons">chevron_left</i>
              </IconButton>
              <IconButton 
                onClick={() => onNavigate && onNavigate('next')} 
                size="small"
                disabled={!hasNext}
                title="Next object"
              >
                <i className="material-icons">chevron_right</i>
              </IconButton>
              <IconButton onClick={onClose} size="small" title="Close" data-testid="dd-detail-close">
                <i className="material-icons">close</i>
              </IconButton>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="detail-sheet-content">
          {isLoading ? (
            <div className="detail-loading">
              <CircularProgress />
              <p>Loading details...</p>
            </div>
          ) : (
            <>
              {!isExpanded ? (
                /* Business-Friendly Summary View */
                <div className="business-summary-view">
                  {/* Description Section */}
                  {objectDetails.description && (
                    <div className="">
                      <h4 className="summary-section-title">Description</h4>
                      <p className="summary-description">{objectDetails.description}</p>
                    </div>
                  )}

                  {/* Key Information */}
                  <div className="summary-card">
                    <h4 className="summary-section-title">Key Information</h4>
                    <div className="key-info-stack">
                      {(objectDetails.displayName || objectDetails.name) && (
                        <div className="info-item-row">
                          <span className="info-label">Name</span>
                          <span className="info-value">{objectDetails.displayName || objectDetails.name}</span>
                        </div>
                      )}
                      {(objectDetails.type || objectDetails.objectType) && (
                        <div className="info-item-row">
                          <span className="info-label">Type</span>
                          <span className="info-value">{objectDetails.type || objectDetails.objectType}</span>
                        </div>
                      )}
                      <div className="info-item-row">
                        <span className="info-label">Source Name</span>
                        <span className="info-value">{objectDetails.sourceName || 'Not Available'}</span>
                      </div>
                      {objectDetails.schemaName && (
                        <div className="info-item-row">
                          <span className="info-label">Schema</span>
                          <span className="info-value">{objectDetails.schemaName}</span>
                        </div>
                      )}
                      <div className="info-item-row">
                        <span className="info-label">Business Context</span>
                        <span className="info-value">{objectDetails.businessContext || 'Not Available'}</span>
                      </div>
                      <div className="info-item-row">
                        <span className="info-label">Business Owner</span>
                        <div className="info-value">
                          {objectDetails.businessOwner ? (
                            Array.isArray(objectDetails.businessOwner) ? (
                              objectDetails.businessOwner.map((owner, idx) => (
                                <div key={idx}>
                                  {typeof owner === 'object' ? `${owner.name}${owner.email ? ` (${owner.email})` : ''}` : owner}
                                </div>
                              ))
                            ) : typeof objectDetails.businessOwner === 'object' ? (
                              <span>{objectDetails.businessOwner.name}{objectDetails.businessOwner.email && ` (${objectDetails.businessOwner.email})`}</span>
                            ) : (
                              <span>{objectDetails.businessOwner}</span>
                            )
                          ) : (
                            <span>Not Available</span>
                          )}
                        </div>
                      </div>
                      <div className="info-item-row">
                        <span className="info-label">Tech Owner</span>
                        <div className="info-value">
                          {objectDetails.techOwner ? (
                            Array.isArray(objectDetails.techOwner) ? (
                              objectDetails.techOwner.map((owner, idx) => (
                                <div key={idx}>
                                  {typeof owner === 'object' ? `${owner.name}${owner.email ? ` (${owner.email})` : ''}` : owner}
                                </div>
                              ))
                            ) : typeof objectDetails.techOwner === 'object' ? (
                              <span>{objectDetails.techOwner.name}{objectDetails.techOwner.email && ` (${objectDetails.techOwner.email})`}</span>
                            ) : (
                              <span>{objectDetails.techOwner}</span>
                            )
                          ) : (
                            <span>Not Available</span>
                          )}
                        </div>
                      </div>
                      {objectDetails.description && (
                        <div className="info-item-row">
                          <span className="info-label">Description</span>
                          <span className="info-value">{objectDetails.description}</span>
                        </div>
                      )}
                      {objectDetails.columns && objectDetails.columns.length > 0 && (
                        <div className="info-item-row">
                          <span className="info-label">Columns</span>
                          <span className="info-value">{objectDetails.columns.length} columns</span>
                        </div>
                      )}
                      {objectDetails.parameters && objectDetails.parameters.length > 0 && (
                        <div className="info-item-row">
                          <span className="info-label">Parameters</span>
                          <span className="info-value">{objectDetails.parameters.length} parameters</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Columns Preview */}
                  {objectDetails.columns && objectDetails.columns.length > 0 && (
                    <div className="summary-card">
                      <h4 className="summary-section-title">Columns ({objectDetails.columns.length})</h4>
                      <div className="columns-preview">
                        {objectDetails.columns.slice(0, 10).map((col, idx) => (
                          <div key={idx} className="column-preview-item">
                            <div className="column-preview-header">
                              <span className="column-preview-name">{col.name || col.columnName}</span>
                              {(col.isPii || col.is_pii) && (
                                <span className="column-preview-type">PII</span>
                              )}
                              {(col.isAiEligible || col.is_ai_eligible) && (
                                <span className="column-preview-type">AI Sensitive</span>
                              )}
                              <span className="column-preview-type">{col.dataType || col.type}</span>
                            </div>
                            <div className="column-business-context">
                              <span className="column-context-label">Business Context:</span>
                              <p className="column-preview-context">{col.businessContext || 'Not Available'}</p>
                            </div>
                          </div>
                        ))}
                        {objectDetails.columns.length > 10 && (
                          <div className="columns-preview-more">
                            <a href="#" onClick={(e) => { e.preventDefault(); toggleExpanded(); }}>
                              View all {objectDetails.columns.length} columns →
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Parameters Preview */}
                  {objectDetails.parameters && objectDetails.parameters.length > 0 && (
                    <div className="summary-card">
                      <h4 className="summary-section-title">Parameters ({objectDetails.parameters.length})</h4>
                      <div className="parameters-preview">
                        {objectDetails.parameters.map((param, idx) => (
                          <div key={idx} className="parameter-preview-item">
                            <div className="parameter-preview-header">
                              <span className="parameter-preview-name">{param.name || param.parameterName}</span>
                              <span className="parameter-preview-meta">
                                {param.dataType || param.type} • {param.parameterMode || param.direction || 'IN'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Technical Details View */
                <div className="technical-details-view">
              {/* Metadata Section - Show all attributes as key-value pairs in 3 columns */}
              <div className="detail-section">
                <h3>Associated Attributes</h3>
                <div className="metadata-grid">
                  {objectDetails && Object.keys(objectDetails).map((key) => {
                    // Skip columns array, internal properties, and any ID fields
                    if (key === 'columns' || key === 'parameters' || key === 'displayName') return null;
                    // Slice 2k: server metadata, not an attribute. Passes every filter below.
                    if (key === 'redactedFields') return null;
                    // #591: drop long-form duplicates — the payload carries both name/objectName,
                    // type/objectType, schema/schemaName. Keep the short form, drop the rest.
                    if (key === 'objectName' || key === 'objectType' || key === 'schemaName') return null;
                    if (key.toLowerCase().includes('id')) return null;
                    
                    const value = objectDetails[key];
                    
                    // Skip null, undefined, empty arrays, and empty objects
                    if (value === null || value === undefined) return null;
                    if (Array.isArray(value) && value.length === 0) return null;
                    if (typeof value === 'object' && Object.keys(value).length === 0) return null;
                    
                    // Format the key from camelCase to readable text
                    const formattedKey = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())
                      .trim();
                    
                    // Format the value
                    let displayValue = value;
                    if (typeof value === 'boolean') {
                      displayValue = value ? 'Yes' : 'No';
                    } else if (Array.isArray(value)) {
                      // Handle arrays of objects (like businessOwner, techOwner)
                      if (value.length > 0 && typeof value[0] === 'object') {
                        displayValue = value.map(item => {
                          if (item.name) {
                            return `${item.name}${item.email ? ` (${item.email})` : ''}`;
                          }
                          return JSON.stringify(item);
                        }).join(', ');
                      } else {
                        displayValue = value.join(', ');
                      }
                    } else if (typeof value === 'object') {
                      // Handle single objects (like businessOwner, techOwner when not array)
                      if (value.name) {
                        displayValue = `${value.name}${value.email ? ` (${value.email})` : ''}`;
                      } else {
                        displayValue = JSON.stringify(value, null, 2);
                      }
                    } else if (typeof value === 'string' && value.trim() === '') {
                      // Handle empty strings
                      displayValue = 'Not Available';
                    }
                    
                    return (
                      <div key={key} className="metadata-item">
                        <span className="detail-label">{formattedKey}</span>
                        <span className="detail-value">{String(displayValue)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Columns Section (for tables/views) */}
              {objectDetails.columns && objectDetails.columns.length > 0 && (
                <div className="detail-section">
                  <h3>Columns ({objectDetails.columns.length})</h3>
                  <div className="table-scroll-container">
                    <div className="columns-table">
                      <table>
                      <thead>
                        <tr>
                           {/* Dynamically generate column headers with businessContext as 3rd column, excluding ID fields and PII keys */}
                          {Object.keys(objectDetails.columns[0]).filter(isAutoRenderedKey).slice(0, 2).map((key) => (
                            <th key={key}>
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}
                            </th>
                          ))}
                          {objectDetails.columns[0].hasOwnProperty('businessContext') && (
                            <th key="businessContext">Business Context</th>
                          )}
                          <th key="pii">PII</th>
                          <th key="aiEligible">AI Sensitive</th>
                          {Object.keys(objectDetails.columns[0]).filter(isAutoRenderedKey).slice(2).map((key) => (
                            <th key={key}>
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {objectDetails.columns.map((col, index) => (
                          <tr key={index}>
                            {/* Render first 2 columns, excluding ID fields and PII keys */}
                             {Object.keys(col).filter(isAutoRenderedKey).slice(0, 2).map((key) => {
                              let value = col[key];
                              if (typeof value === 'boolean') {
                                value = value ? 'Yes' : 'No';
                              } else if (value === null || value === undefined) {
                                value = '-';
                              }
                              return <td key={key}>{String(value)}</td>;
                            })}
                            {/* Render businessContext as 3rd column */}
                            {col.hasOwnProperty('businessContext') && (
                              <td key="businessContext">{String(col.businessContext || '-')}</td>
                            )}
                            {/* Render PII badge column */}
                            <td key="pii">
                              {(col.isPii || col.is_pii) && (
                                <span className="pii-badge">PII</span>
                              )}
                            </td>
                            {/* Render AI Eligible badge column */}
                            <td key="aiEligible">
                              {(col.isAiEligible || col.is_ai_eligible) && (
                                <span className="ai-eligible-badge">AI Sensitive</span>
                              )}
                            </td>
                            {/* Render remaining columns, excluding ID fields and PII keys */}
                            {Object.keys(col).filter(isAutoRenderedKey).slice(2).map((key) => {
                              let value = col[key];
                              if (typeof value === 'boolean') {
                                value = value ? 'Yes' : 'No';
                              } else if (value === null || value === undefined) {
                                value = '-';
                              }
                              return <td key={key}>{String(value)}</td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Change History Section — recent edits to this object and its columns */}
              <div className="detail-section">
                <h3>Change History{Array.isArray(objectHistory) ? ` (${objectHistory.length})` : ''}</h3>
                {isLoadingHistory ? (
                  <div className="detail-loading">
                    <CircularProgress />
                    <p>Loading change history...</p>
                  </div>
                ) : (
                  <div className="table-scroll-container">
                    <div className="change-history-table">
                      <table>
                        <thead>
                          <tr>
                            <th>When</th>
                            <th>Type</th>
                            <th>Entity</th>
                            <th>Change</th>
                            <th>By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(objectHistory) && objectHistory.length > 0 ? (
                            objectHistory.map((entry) => (
                              <tr key={entry.versionId}>
                                <td>{formatHistoryDate(entry.changedAt)}</td>
                                <td><span className="change-type-badge">{entry.changeType}</span></td>
                                <td>
                                  {entry.entityName}
                                  <span className="change-entity-kind">{entry.entityType === 'COLUMN' ? 'Column' : 'Table'}</span>
                                </td>
                                <td>{summarizeChange(entry)}</td>
                                <td>{entry.changedBy || '-'}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="change-history-empty">No recorded changes for this object.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Parameters Section (for procedures/functions) */}
              {objectDetails.parameters && objectDetails.parameters.length > 0 && (
                <div className="detail-section">
                  <h3>Parameters ({objectDetails.parameters.length})</h3>
                  <div className="table-scroll-container">
                    <div className="parameters-table">
                      <table>
                      <thead>
                        <tr>
                          {Object.keys(objectDetails.parameters[0]).filter(key => !key.toLowerCase().includes('id')).map((key) => (
                            <th key={key}>
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {objectDetails.parameters.map((param, index) => (
                          <tr key={index}>
                            {Object.keys(param).filter(key => !key.toLowerCase().includes('id')).map((key) => (
                              <td key={key}>{String(param[key] || '-')}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {isLoadingRelated && (
                <div className="detail-loading">
                  <CircularProgress />
                  <p>Loading related metadata...</p>
                </div>
              )}

              {!isLoadingRelated && objectRelated && objectRelated.indexes && objectRelated.indexes.length > 0 && (
                <div className="detail-section">
                  <h3>Indexes ({objectRelated.indexes.length})</h3>
                  <div className="table-scroll-container">
                    <div className="indexes-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Index Name</th>
                            <th>Type</th>
                            <th>Unique</th>
                            <th>Columns</th>
                          </tr>
                        </thead>
                        <tbody>
                          {objectRelated.indexes.map((index, idx) => (
                            <tr key={index.indexId || idx}>
                              <td>{index.indexName || '-'}</td>
                              <td>{index.indexType || '-'}</td>
                              <td>{index.isUnique ? 'Yes' : 'No'}</td>
                              <td>{index.columnsText || ((index.columns || []).join(', ') || '-')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!isLoadingRelated && objectRelated && objectRelated.foreignKeys && objectRelated.foreignKeys.length > 0 && (
                <div className="detail-section">
                  <h3>Foreign Keys ({objectRelated.foreignKeys.length})</h3>
                  <div className="table-scroll-container">
                    <div className="foreign-keys-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Constraint Name</th>
                            <th>Column</th>
                            <th>References Table</th>
                            <th>References Column</th>
                            <th>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {objectRelated.foreignKeys.map((foreignKey, idx) => (
                            <tr key={foreignKey.relationshipId || idx}>
                              <td>{foreignKey.constraintName || '-'}</td>
                              <td>{foreignKey.columnName || '-'}</td>
                              <td>{foreignKey.referencesTable || '-'}</td>
                              <td>{foreignKey.referencesColumn || '-'}</td>
                              <td>{foreignKey.relationshipType || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!isLoadingRelated && objectRelated && objectRelated.triggers && objectRelated.triggers.length > 0 && (
                <div className="detail-section">
                  <h3>Triggers ({objectRelated.triggers.length})</h3>
                  <div className="table-scroll-container">
                    <div className="triggers-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Event</th>
                            <th>Timing</th>
                            <th>Definer</th>
                            <th>Statement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {objectRelated.triggers.map((trigger, idx) => {
                            const triggerKey = trigger.objectId || idx;
                            const isStatementExpanded = !!expandedTriggerStatements[triggerKey];
                            return (
                              <tr key={triggerKey}>
                                <td>{trigger.name || '-'}</td>
                                <td>{trigger.event || '-'}</td>
                                <td>{trigger.timing || '-'}</td>
                                <td>{trigger.definer || '-'}</td>
                                <td>
                                  {trigger.statement ? (
                                    <>
                                      <a
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          toggleTriggerStatement(triggerKey);
                                        }}
                                        className="expand-link"
                                      >
                                        {isStatementExpanded ? 'Hide statement' : 'View statement'}
                                      </a>
                                      {isStatementExpanded && (
                                        <pre className="trigger-statement">{trigger.statement}</pre>
                                      )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!isLoadingRelated && objectRelated && objectRelated.constraints && objectRelated.constraints.length > 0 && (
                <div className="detail-section">
                  <h3>Constraints ({objectRelated.constraints.length})</h3>
                  <div className="table-scroll-container">
                    <div className="constraints-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Constraint Name</th>
                            <th>Type</th>
                            <th>Definition</th>
                          </tr>
                        </thead>
                        <tbody>
                          {objectRelated.constraints.map((constraint, idx) => (
                            <tr key={constraint.constraintName || idx}>
                              <td>{constraint.constraintName || '-'}</td>
                              <td>{constraint.constraintType || '-'}</td>
                              <td>{constraint.definition || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ObjectDetailSheet;
